import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmail, createAccount } from '@paulownia/shared/lib/auth';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@paulownia/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@paulownia/shared/components/ui/tabs';
import { toast } from 'sonner';

export const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            await signInWithEmail(email, password);

            toast.success('Connexion réussie');
            navigate('/dashboard');
        } catch (error) {
            console.error('Login error:', error);
            const authError = error as { code?: string };

            // Handle specific Firebase auth errors
            let errorMessage = 'Erreur de connexion';

            if (authError.code === 'auth/invalid-credential' || authError.code === 'auth/wrong-password') {
                errorMessage = 'Email ou mot de passe incorrect';
            } else if (authError.code === 'auth/user-not-found') {
                errorMessage = 'Utilisateur non trouvé';
            } else if (authError.code === 'auth/invalid-email') {
                errorMessage = 'Email invalide';
            } else if (authError.code === 'auth/too-many-requests') {
                errorMessage = 'Trop de tentatives. Réessayez plus tard';
            } else if (authError.code === 'auth/network-request-failed') {
                errorMessage = 'Erreur réseau. Vérifiez votre connexion';
            }

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            await createAccount(email, password);

            toast.success('Compte créé avec succès');
            navigate('/dashboard');
        } catch (error) {
            console.error('Sign up error:', error);
            const authError = error as { code?: string };

            // Handle specific Firebase auth errors
            let errorMessage = 'Erreur lors de la création du compte';

            if (authError.code === 'auth/email-already-in-use') {
                errorMessage = 'Cet email est déjà utilisé';
            } else if (authError.code === 'auth/invalid-email') {
                errorMessage = 'Email invalide';
            } else if (authError.code === 'auth/weak-password') {
                errorMessage = 'Le mot de passe est trop faible (minimum 6 caractères)';
            } else if (authError.code === 'auth/network-request-failed') {
                errorMessage = 'Erreur réseau. Vérifiez votre connexion';
            }

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle className="text-2xl text-center">Dashboard</CardTitle>
                </CardHeader>
                <CardContent>
                    <Tabs defaultValue="signin" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="signin">Connexion</TabsTrigger>
                            <TabsTrigger value="signup">Créer un compte</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="signin">
                            <form onSubmit={handleSignIn} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <label htmlFor="email-signin" className="text-sm font-medium">
                                        Email
                                    </label>
                                    <Input
                                        id="email-signin"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="nom@exemple.com"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="password-signin" className="text-sm font-medium">
                                        Mot de passe
                                    </label>
                                    <Input
                                        id="password-signin"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Mot de passe"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <Button type="submit" className="w-full" disabled={loading}>
                                    {loading ? 'Connexion...' : 'Se connecter'}
                                </Button>
                                <div className="text-center">
                                    <Link to="/reset-password" className="text-sm text-muted-foreground hover:underline">
                                        Mot de passe oublié ?
                                    </Link>
                                </div>
                            </form>
                        </TabsContent>
                        
                        <TabsContent value="signup">
                            <form onSubmit={handleSignUp} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <label htmlFor="email-signup" className="text-sm font-medium">
                                        Email
                                    </label>
                                    <Input
                                        id="email-signup"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="nom@exemple.com"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="password-signup" className="text-sm font-medium">
                                        Mot de passe
                                    </label>
                                    <Input
                                        id="password-signup"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Minimum 6 caractères"
                                        required
                                        disabled={loading}
                                        minLength={6}
                                    />
                                </div>
                                <Button type="submit" className="w-full" disabled={loading}>
                                    {loading ? 'Création...' : 'Créer le compte'}
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
};
